'use client';

import {
  faFacebookF,
  faLinkedinIn,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Container,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';

/**
 * Footer Component - Organism Design Pattern
 *
 * A comprehensive footer component based on the Figma design for Bách Việt.
 * Contains company information, navigation links, contact details, and social media icons.
 * Matches the exact layout and content from the Figma design.
 *
 * @param className - Additional CSS classes
 */
export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterProps {
  companyName?: string;
  companyDescription?: string;
  contactInfo?: {
    address: string;
    phone: string;
    email: string;
  };
  socialLinks?: Array<{
    platform: string;
    url: string;
  }>;
  footerSections?: Array<{
    title: string;
    links: FooterLink[];
  }>;
  copyright?: string;
}

const Footer = ({
  companyName,
  companyDescription,
  contactInfo,
  socialLinks,
  footerSections,
  copyright,
}: FooterProps) => {
  return (
    <Box
      id='footer-container'
      component='footer'
      sx={{
        backgroundColor: '#160000',
        color: 'white',
        width: '100%',
      }}
    >
      {/* Main Footer Content */}
      <Box
        id='footer-main-content'
        sx={{
          backgroundColor: '#160000',
          minHeight: 'auto',
          paddingTop: 4,
          paddingBottom: 4,
          '@media (min-width: 900px)': {
            minHeight: '460px',
            paddingTop: 21,
            paddingBottom: 12,
          },
        }}
      >
        <Container id='footer-container-inner' maxWidth='xl'>
          <Box
            id='footer-content-grid'
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 4,
              alignItems: 'start',
              margin: '0 auto',
              '@media (min-width: 900px)': {
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 6,
                paddingLeft: { xs: 3, sm: 4, md: '136px', lg: '321px' },
              },
            }}
          >
            {/* Company Information Section */}
            <Box
              id='footer-company-info'
              sx={{
                gridColumn: '1',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: { xs: 'auto', md: '460px' },
                '@media (min-width: 900px)': {
                  gridColumn: '1',
                },
              }}
            >
              <Box>
                <Typography
                  id='footer-company-name'
                  variant='h6'
                  sx={{
                    fontSize: '24px',
                    fontWeight: 700,
                    fontFamily: 'var(--font-inter), Inter, sans-serif',
                    lineHeight: '1.25em',
                    mb: 4,
                    color: 'white',
                    maxWidth: '100%',
                    '@media (min-width: 900px)': {
                      maxWidth: '478px',
                    },
                    minHeight: '60px',
                  }}
                >
                  {companyName}
                </Typography>

                <Typography
                  id='footer-company-description'
                  variant='body2'
                  sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    fontFamily: 'var(--font-inter), Inter, sans-serif',
                    lineHeight: '1.25em',
                    mb: 6,
                    color: 'white',
                    opacity: 0.9,
                    maxWidth: '100%',
                    '@media (min-width: 900px)': {
                      maxWidth: '315px',
                    },
                    minHeight: '80px',
                  }}
                >
                  {companyDescription}
                </Typography>

                {/* Social Media Icons */}
                <Box
                  id='footer-social-icons'
                  sx={{
                    display: 'flex',
                    gap: '32px',
                    alignItems: 'center',
                    maxWidth: '136px',
                    minHeight: '24px',
                  }}
                >
                  {socialLinks?.map((socialLink, _index) => {
                    // Map platform to FontAwesome icon
                    const getIcon = (platform: string) => {
                      switch (platform.toLowerCase()) {
                        case 'facebook':
                          return faFacebookF;
                        case 'linkedin':
                          return faLinkedinIn;
                        case 'youtube':
                          return faYoutube;
                        default:
                          return faFacebookF;
                      }
                    };

                    return (
                      <IconButton
                        key={socialLink.platform}
                        id={`footer-${socialLink.platform}-icon`}
                        href={socialLink.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label={`Visit our ${socialLink.platform} page`}
                        sx={{
                          color: 'white',
                          width: '24px',
                          height: '24px',
                          padding: 0,
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            opacity: 0.8,
                          },
                        }}
                      >
                        <FontAwesomeIcon
                          icon={getIcon(socialLink.platform)}
                          style={{
                            width: '24px',
                            height: '24px',
                          }}
                        />
                      </IconButton>
                    );
                  })}
                </Box>
              </Box>

              {/* Copyright aligned with company name */}
              <Typography
                id='footer-copyright-text'
                variant='body2'
                sx={{
                  fontSize: '16px',
                  fontWeight: 400,
                  fontFamily: 'var(--font-inter), Inter, sans-serif',
                  lineHeight: '1.21em',
                  color: 'white',
                  opacity: 0.9,
                  maxWidth: '100%',
                  minHeight: '19px',
                  whiteSpace: 'nowrap',
                  '@media (min-width: 900px)': {
                    maxWidth: '600px',
                  },
                }}
              >
                {copyright}
              </Typography>
            </Box>

            {/* Contact Information Section */}
            <Box
              id='footer-contact-info'
              sx={{
                gridColumn: '1',
                maxWidth: '100%',
                '@media (min-width: 900px)': {
                  gridColumn: '2',
                  maxWidth: '285px',
                },
              }}
            >
              <Typography
                id='footer-contact-title'
                variant='h6'
                sx={{
                  fontSize: '24px',
                  fontWeight: 700,
                  fontFamily: 'var(--font-inter), Inter, sans-serif',
                  lineHeight: '1.21em',
                  mb: 4,
                  color: 'white',
                  maxWidth: '85px',
                  minHeight: '29px',
                }}
              >
                Liên hệ
              </Typography>

              <Typography
                id='footer-address'
                variant='body2'
                sx={{
                  fontSize: '16px',
                  fontWeight: 400,
                  fontFamily: 'var(--font-inter), Inter, sans-serif',
                  lineHeight: '1.25em',
                  mb: 4,
                  color: 'white',
                  opacity: 0.9,
                  maxWidth: '100%',
                  minHeight: '60px',
                  '@media (min-width: 900px)': {
                    maxWidth: '285px',
                  },
                }}
              >
                {contactInfo?.address}
              </Typography>

              <Typography
                id='footer-phone'
                variant='body2'
                sx={{
                  fontSize: '16px',
                  fontWeight: 700,
                  fontFamily: 'var(--font-inter), Inter, sans-serif',
                  lineHeight: '1.21em',
                  mb: 3,
                  color: 'white',
                  maxWidth: '100%',
                  minHeight: '19px',
                  '@media (min-width: 900px)': {
                    maxWidth: '285px',
                  },
                }}
              >
                Điện thoại: {contactInfo?.phone}
              </Typography>

              <Typography
                id='footer-email'
                variant='body2'
                sx={{
                  fontSize: '16px',
                  fontWeight: 400,
                  fontFamily: 'var(--font-inter), Inter, sans-serif',
                  lineHeight: '1.3em',
                  color: 'white',
                  opacity: 0.9,
                  maxWidth: '100%',
                  minHeight: '19px',
                  '@media (min-width: 900px)': {
                    maxWidth: '285px',
                  },
                }}
              >
                Email: {contactInfo?.email}
              </Typography>
            </Box>

            {/* Dynamic Footer Sections */}
            {footerSections?.map((section, sectionIndex) => (
              <Box
                key={section.title}
                id={`footer-${section.title.toLowerCase().replace(/\s+/g, '-')}-section`}
                sx={{
                  gridColumn: '1',
                  maxWidth: '100%',
                  '@media (min-width: 900px)': {
                    gridColumn: sectionIndex + 3, // Start from column 3
                    maxWidth: '200px',
                  },
                }}
              >
                <Typography
                  id={`footer-${section.title.toLowerCase().replace(/\s+/g, '-')}-title`}
                  variant='h6'
                  sx={{
                    fontSize: '24px',
                    fontWeight: 700,
                    fontFamily: 'var(--font-inter), Inter, sans-serif',
                    lineHeight: '1.21em',
                    mb: 4,
                    color: 'white',
                    maxWidth: '200px',
                    minHeight: '29px',
                  }}
                >
                  {section.title}
                </Typography>

                <Stack
                  id={`footer-${section.title.toLowerCase().replace(/\s+/g, '-')}-links`}
                  spacing={3}
                  sx={{
                    '& a': {
                      color: 'white',
                      textDecoration: 'none',
                      fontSize: '16px',
                      fontWeight: 400,
                      fontFamily: 'var(--font-inter), Inter, sans-serif',
                      lineHeight: '1.21em',
                      opacity: 0.9,
                      transition: 'opacity 0.2s',
                      height: '19px',
                      '&:hover': {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {section.links.map((link, linkIndex) => (
                    <Link
                      key={link.label}
                      href={link.href}
                      id={`footer-${section.title.toLowerCase().replace(/\s+/g, '-')}-link-${linkIndex}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
